import React, { useState, useEffect, useRef } from "react";
import { Alert, Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Container,
  Card,
  CardBody,
} from "reactstrap";
import Instructions from "./instructions";
import usePermission from "hooks/usePermission";
import {
  getBombLineCount,
  getSettings,
  updateJackpotSettings,
  uploadFile,
} from "store/actions";
import LoadingButton from "../../../components/shared/LoadingButton";
import { FaEdit, FaSave, FaTimes } from "react-icons/fa";

const JackpotSettings = () => {
  const dispatch = useDispatch();
  const { settings, loading, error, bombCountSettings, bombCountLoading } =
    useSelector(({ Settings }) => Settings);
  const { hasPermission } = usePermission();
  const fileInputRef = useRef(null);
  const [jackpotItems, setJackpotItems] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedItem, setEditedItem] = useState({});
  const permissions = {
    add: false,
    edit: hasPermission("edit_jackpot_setting"),
    delete: false,
  };

  const [fields, setFields] = useState({
    rows: 0,
    columns: 0,
    items: [],
    canWinGrand: false,
    canWinMajor: false,
    canWinMinor: false,
    status: "disabled",
    startingJackpotLine: 1,
    endingJackpotLine: 10,
  });

  useEffect(() => {
    dispatch(getSettings());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getBombLineCount());
  }, [dispatch]);

  useEffect(() => {
    if (settings?._id) {
      const jackpot = settings.jackpot;
      setFields(() => ({
        rows: jackpot?.rows,
        columns: jackpot?.columns,
        items: jackpot?.items,
        canWinGrand: jackpot?.canWinGrand,
        canWinMajor: jackpot?.canWinMajor,
        canWinMinor: jackpot?.canWinMinor,
        status: jackpot?.status,
        startingJackpotLine: jackpot?.startingJackpotLine,
        endingJackpotLine: jackpot?.endingJackpotLine,
      }));
      setJackpotItems(jackpot.items);
    }
  }, [settings]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFields((prev) => ({
      ...prev,
      [name]: Number(value) || "",
    }));
  };

  const handleBooleanChange = (event) => {
    const { name, value } = event.target;
    setFields((prev) => ({
      ...prev,
      [name]: value || false,
    }));
  };

  const handleEditClick = (index) => {
    setEditingIndex(index);
    setEditedItem({ ...fields.items[index] });
  };

  const handleInputChange = (field, value) => {
    setEditedItem((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectChange = (event) => {
    const { name, value } = event.target;
    setFields((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileUpload = async (field, file) => {
    if (!file) return;
    // console.info("file", file);
    try {
      const response = await dispatch(uploadFile({ data: file }));
      // console.info("response", response);
      const uploadedUrl = response?.payload?.url;
      // console.info("uploadedUrl", uploadedUrl);
      if (uploadedUrl) {
        setEditedItem((prev) => ({
          ...prev,
          [field]: uploadedUrl,
        }));
      }
    } catch (error) {
      console.error("File upload failed:", error);
    }
  };

  const handleSave = () => {
    const updatedItems = [...fields.items];
    updatedItems[editingIndex] = editedItem;
    setFields((prev) => ({
      ...prev,
      items: updatedItems,
    }));
    setJackpotItems(updatedItems);
    setEditingIndex(null);
    setEditedItem({});
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setEditedItem({});
  };

  const submit = async () => {
    dispatch(
      updateJackpotSettings({
        data: { ...fields },
      }),
    );
  };

  const bombLines = bombCountSettings?.data || [];

  const firstBombLine = bombLines.find((item) => item.bombCount === 3);

  const maxAllowedLine = firstBombLine ? firstBombLine.lineNumber - 1 : null;
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col sm={12} lg={12}>
              <Card>
                <CardBody className="spinner-content">
                  {error && typeof error === "string" ? (
                    <Alert color="danger">{error}</Alert>
                  ) : null}

                  {loading && <div className="spinner"></div>}

                  <Col lg={12}>
                    <Instructions />
                  </Col>

                  <Col lg={12}>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Row Count</Label>
                          <Input
                            type="number"
                            placeholder="Row Count"
                            value={fields?.rows}
                            name="rows"
                            onChange={handleChange}
                          />
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label>Column Count</Label>
                          <Input
                            type="number"
                            placeholder="Column Count"
                            value={fields?.columns}
                            name="columns"
                            onChange={handleChange}
                          />
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label>Can Win Epic (Grand)</Label>
                          <div className="d-flex align-items-center gap-10">
                            <input
                              type="checkbox"
                              checked={fields?.canWinGrand || false}
                              id="canWinGrand"
                              className="form-check"
                              onClick={() => {
                                const newValue = !fields?.canWinGrand;
                                console.info(
                                  "canWinGrand newValue ---->",
                                  newValue,
                                );
                                handleBooleanChange({
                                  target: {
                                    name: "canWinGrand",
                                    value: newValue,
                                  },
                                });
                              }}
                            />
                            <label
                              htmlFor="canWinGrand"
                              className="form-label m-0"
                            >
                              {fields?.canWinGrand ? "Enabled" : "Disabled"}
                            </label>
                          </div>
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label>Can Win Elite (Major)</Label>
                          <div className="d-flex align-items-center gap-10">
                            <input
                              type="checkbox"
                              checked={fields?.canWinMajor || false}
                              id="canWinMajor"
                              className="form-check"
                              onClick={() => {
                                const newValue = !fields?.canWinMajor;
                                console.info(
                                  "canWinMajor newValue ---->",
                                  newValue,
                                );
                                handleBooleanChange({
                                  target: {
                                    name: "canWinMajor",
                                    value: newValue,
                                  },
                                });
                              }}
                            />
                            <label
                              htmlFor="canWinMajor"
                              className="form-label m-0"
                            >
                              {fields?.canWinMajor ? "Enabled" : "Disabled"}
                            </label>
                          </div>
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label>Can Win Basic (Minor)</Label>
                          <div className="d-flex align-items-center gap-10">
                            <input
                              type="checkbox"
                              checked={fields?.canWinMinor || false}
                              id="canWinMinor"
                              className="form-check"
                              onClick={() => {
                                const newValue = !fields?.canWinMinor;
                                console.info(
                                  "canWinMinor newValue ---->",
                                  newValue,
                                );
                                handleBooleanChange({
                                  target: {
                                    name: "canWinMinor",
                                    value: newValue,
                                  },
                                });
                              }}
                            />
                            <label
                              htmlFor="canWinMinor"
                              className="form-label m-0"
                            >
                              {fields?.canWinMinor ? "Enabled" : "Disabled"}
                            </label>
                          </div>
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label>Jackpot Status</Label>
                          <select
                            style={{ height: 50 }}
                            name="status"
                            className="form-control"
                            value={fields?.status}
                            onChange={handleSelectChange}
                          >
                            <option value="enabled">Enabled</option>
                            <option value="disabled">Disabled</option>
                          </select>
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label>Starting Jackpot Line</Label>
                          {/* <Input
                            type="number"
                            placeholder="Starting Jackpot Line Number"
                            value={fields?.startingJackpotLine}
                            name="startingJackpotLine"
                            onChange={handleChange}
                          /> */}
                          <Input
                            type="number"
                            placeholder="Starting Jackpot Line Number"
                            value={fields?.startingJackpotLine}
                            name="startingJackpotLine"
                            min={1}
                            max={maxAllowedLine ?? undefined}
                            disabled={maxAllowedLine === 0}
                            onChange={handleChange}
                          />
                        </FormGroup>
                      </Col>

                      <Col md={6}>
                        <FormGroup>
                          <Label>Ending Jackpot Line</Label>
                          <Input
                            type="number"
                            placeholder="Ending Jackpot Line Number"
                            value={fields?.endingJackpotLine}
                            name="endingJackpotLine"
                            max={maxAllowedLine ?? undefined}
                            disabled={maxAllowedLine === 0}
                            onChange={handleChange}
                          />
                        </FormGroup>
                      </Col>

                      {fields.items.map((item, index) => {
                        const isEditing = editingIndex === index;

                        return (
                          <div
                            key={index}
                            className="d-flex align-items-start mb-3 p-2 w-25 gap-10 border rounded position-relative"
                            style={{ marginRight: 5 }}
                          >
                            {isEditing ? (
                              <>
                                <img
                                  src={
                                    editedItem.icon
                                      ? typeof editedItem.icon === "string"
                                        ? editedItem.icon
                                        : URL.createObjectURL(editedItem.icon)
                                      : null
                                  }
                                  alt="icon"
                                  style={{
                                    width: 40,
                                    height: 40,
                                    objectFit: "contain",
                                    marginRight: 12,
                                    cursor: "pointer",
                                  }}
                                  onClick={() => fileInputRef.current.click()}
                                />
                                <input
                                  type="file"
                                  accept="image/*"
                                  style={{ display: "none" }}
                                  ref={fileInputRef}
                                  onChange={(e) =>
                                    handleFileUpload("icon", e.target.files[0])
                                  }
                                />
                              </>
                            ) : (
                              item.icon && (
                                <img
                                  src={
                                    typeof item.icon === "string"
                                      ? item.icon
                                      : URL.createObjectURL(item.icon)
                                  }
                                  alt="icon"
                                  style={{
                                    width: 40,
                                    height: 40,
                                    objectFit: "contain",
                                    marginRight: 12,
                                  }}
                                />
                              )
                            )}

                            <div style={{ flex: 1 }}>
                              <p className="mb-1">
                                <strong>Type:</strong>{" "}
                                {isEditing ? (
                                  <Input
                                    type="text"
                                    bsSize="sm"
                                    value={editedItem.type}
                                    disabled
                                    onChange={(e) =>
                                      handleInputChange("type", e.target.value)
                                    }
                                  />
                                ) : (
                                  <>
                                    {item?.type === "grand"
                                      ? `EPIC (${item?.type?.toUpperCase()})`
                                      : item?.type === "major"
                                        ? `ELITE (${item?.type?.toUpperCase()})`
                                        : item?.type === "minor"
                                          ? `BASIC (${item?.type?.toUpperCase()})`
                                          : item?.type?.toUpperCase()}
                                    {/* {item?.type?.toUpperCase()} */}
                                  </>
                                )}
                              </p>
                              <p className="mb-1">
                                <strong>Count:</strong>{" "}
                                {isEditing ? (
                                  <Input
                                    type="number"
                                    bsSize="sm"
                                    value={editedItem.count}
                                    onChange={(e) =>
                                      handleInputChange("count", e.target.value)
                                    }
                                  />
                                ) : (
                                  item?.count
                                )}
                              </p>
                              <p className="mb-1">
                                <strong>Min:</strong>{" "}
                                {isEditing ? (
                                  <Input
                                    type="number"
                                    bsSize="sm"
                                    value={editedItem.min}
                                    onChange={(e) =>
                                      handleInputChange("min", e.target.value)
                                    }
                                  />
                                ) : (
                                  item?.min
                                )}
                              </p>
                              <p className="mb-0">
                                <strong>Max:</strong>{" "}
                                {isEditing ? (
                                  <Input
                                    type="number"
                                    bsSize="sm"
                                    value={editedItem.max}
                                    onChange={(e) =>
                                      handleInputChange("max", e.target.value)
                                    }
                                  />
                                ) : (
                                  item?.max
                                )}
                              </p>
                            </div>

                            {permissions.edit && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: 8,
                                  right: 8,
                                }}
                              >
                                {isEditing ? (
                                  <>
                                    <Button
                                      size="sm"
                                      color="success"
                                      className="me-1"
                                      onClick={handleSave}
                                    >
                                      <FaSave />
                                    </Button>
                                    <Button
                                      size="sm"
                                      color="secondary"
                                      onClick={handleCancel}
                                    >
                                      <FaTimes />
                                    </Button>
                                  </>
                                ) : (
                                  <Button
                                    size="sm"
                                    color="primary"
                                    onClick={() => handleEditClick(index)}
                                  >
                                    <FaEdit />
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      <Col md={6} />
                      {permissions.edit && (
                        <Col className="d-flex justify-content-end mt-3">
                          <LoadingButton
                            onClick={submit}
                            className="btn btn-primary btn-lg"
                            loading={loading}
                            disabled={loading}
                          >
                            {!loading ? <>Save Changes</> : ""}
                          </LoadingButton>
                        </Col>
                      )}
                    </Row>
                  </Col>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default JackpotSettings;
